/**
 * Text canonicalization for menu search: indexed text and query are folded to the same
 * lowercase Uzbek-Latin form, so Cyrillic and Latin spellings of one dish meet
 * («лагман» ↔ «lag'mon»). Each canonical char keeps its original span, so match ranges
 * map back onto the original string for highlighting.
 */

/** A [start, end) character span in the ORIGINAL string. */
export interface HighlightRange {
  start: number;
  end: number;
}

export interface CanonicalText {
  /** The canonical form: lowercase Latin, no apostrophes / soft signs. */
  text: string;
  /** Per canonical char: start index (in the original) of the char that produced it. */
  starts: number[];
  /** Per canonical char: end index (exclusive, in the original) of the char that produced it. */
  ends: number[];
}

/**
 * Cyrillic → Latin, Uzbek-style digraphs (ж→j, х→x); Russian romanization habits (zh, kh)
 * land one edit away, which fuzzy matching absorbs. ё folds into «е» (the classic rule).
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'j', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'x', ц: 's', ч: 'ch', ш: 'sh',
  щ: 'sh', ы: 'i', э: 'e', ю: 'yu', я: 'ya',
  // Uzbek Cyrillic; ғ/ў lose their Latin apostrophe (gʻ/oʻ) because apostrophes are dropped.
  қ: 'q', ғ: 'g', ў: 'o', ҳ: 'h',
};

/** Confusable-consonant folds: z→s (pizza↔pitsa↔пицца), x→h (х/ҳ mixups). */
const FOLD: Record<string, string> = { z: 's', x: 'h' };

/** Apostrophe lookalikes used in Uzbek Latin (oʻ, g', o'), plus ь/ъ — all dropped. */
const DROPPED = new Set(["'", '`', '´', 'ʻ', 'ʼ', '‘', '’', 'ь', 'ъ']);

export function canonicalize(source: string): CanonicalText {
  let text = '';
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;
  for (const char of source) {
    const end = index + char.length;
    for (const lower of char.toLowerCase()) {
      if (!DROPPED.has(lower)) {
        const mapped = CYRILLIC_TO_LATIN[lower] ?? lower;
        for (const raw of mapped) {
          const out = FOLD[raw] ?? raw;
          // Collapse doubled letters (pizza→pisa) — letters only, «22» must stay exact.
          if (out >= 'a' && out <= 'z' && out === text[text.length - 1]) continue;
          text += out;
          // One entry per UTF-16 code unit, not per code point: consumers address the
          // canonical text by code unit, and an astral char (🌶) emits two.
          for (let unit = 0; unit < out.length; unit++) {
            starts.push(index);
            ends.push(end);
          }
        }
      }
    }
    index = end;
  }
  return { text, starts, ends };
}

/** Maps a [start, end) span of the canonical text back onto the original string. */
export function toOriginalRange(
  canonical: CanonicalText,
  start: number,
  end: number,
): HighlightRange | null {
  if (start < 0 || end > canonical.text.length || start >= end) return null;
  return { start: canonical.starts[start], end: canonical.ends[end - 1] };
}

export interface TextSegment {
  text: string;
  hit: boolean;
}

/** Splits `source` into plain/highlighted segments; unsorted/overlapping ranges are merged. */
export function splitByRanges(source: string, ranges: readonly HighlightRange[]): TextSegment[] {
  if (ranges.length === 0) return source ? [{ text: source, hit: false }] : [];

  const merged: HighlightRange[] = [];
  for (const range of [...ranges].sort((a, b) => a.start - b.start)) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push({ ...range });
  }

  const segments: TextSegment[] = [];
  let pos = 0;
  for (const range of merged) {
    const start = Math.min(Math.max(range.start, pos), source.length);
    const end = Math.min(Math.max(range.end, start), source.length);
    if (start > pos) segments.push({ text: source.slice(pos, start), hit: false });
    if (end > start) segments.push({ text: source.slice(start, end), hit: true });
    pos = Math.max(pos, end);
  }
  if (pos < source.length) segments.push({ text: source.slice(pos), hit: false });
  return segments;
}
